import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Flag, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

const REASONS = [
  { id: "scam_fraud", label: "Fraude ou esquema" },
  { id: "harassment", label: "Assédio ou ameaças" },
  { id: "hate_speech", label: "Discurso de ódio ou discriminação" },
  { id: "illegal_content", label: "Conteúdo ilegal" },
  { id: "impersonation", label: "Falsa identidade" },
  { id: "privacy_violation", label: "Violação de privacidade" },
  { id: "spam", label: "Spam ou publicidade enganosa" },
  { id: "off_platform_payment", label: "Tentativa de pagamento fora da plataforma" },
  { id: "inappropriate", label: "Inapropriado" },
  { id: "other", label: "Outro" },
];

/**
 * Report button + modal. Use anywhere a piece of user-generated content is displayed.
 * Props: target_type ('event'|'business'|'job'|'classified'|'community'|'user'|'product'|'service'|'review'), target_id, className?
 */
export default function ReportButton({ target_type, target_id, className = "", open: externalOpen, onOpenChange, hideTrigger = false }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [internalOpen, setInternalOpen] = useState(false);
  const open = externalOpen ?? internalOpen;
  const setOpen = (v) => { setInternalOpen(v); onOpenChange?.(v); };
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const openModal = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!user) {
      toast.info("Entra na tua conta para denunciar.");
      nav("/login");
      return;
    }
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!reason) return toast.error("Escolhe um motivo");
    setBusy(true);
    try {
      await api.post("/reports", { target_type, target_id, reason, notes });
      toast.success("Denúncia enviada. A nossa equipa vai analisar.");
      setOpen(false);
      setReason("");
      setNotes("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erro ao enviar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {!hideTrigger && (
      <button
        type="button"
        data-testid={`report-btn-${target_type}-${target_id}`}
        onClick={openModal}
        className={`inline-flex items-center gap-1 text-[11px] font-semibold text-zinc-500 hover:text-[#c8102e] transition-colors ${className}`}
      >
        <Flag size={12} /> Denunciar
      </button>
      )}

      {open && (
        <div
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setOpen(false)}
        >
          <div
            data-testid="report-modal"
            className="bg-white rounded-2xl w-full max-w-md p-5 shadow-xl max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="font-display font-extrabold text-lg">Denunciar conteúdo</h2>
                <p className="text-xs text-zinc-500 mt-1">
                  A tua denúncia é confidencial. A equipa de moderação vai analisar.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="report-close"
                className="p-1 rounded-full hover:bg-zinc-100"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={submit} className="space-y-3">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">
                  Motivo *
                </div>
                <div className="grid grid-cols-1 gap-1">
                  {REASONS.map((r) => (
                    <label
                      key={r.id}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors ${
                        reason === r.id
                          ? "border-black bg-zinc-50 font-semibold"
                          : "border-zinc-200 hover:border-zinc-400"
                      }`}
                    >
                      <input
                        type="radio"
                        name="reason"
                        value={r.id}
                        checked={reason === r.id}
                        onChange={() => setReason(r.id)}
                        data-testid={`report-reason-${r.id}`}
                        className="accent-[#c8102e]"
                      />
                      <span>{r.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">
                  Detalhes (opcional)
                </div>
                <textarea
                  data-testid="report-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value.slice(0, 1000))}
                  rows={3}
                  placeholder="Podes descrever brevemente o que se passou."
                  className="w-full px-3 py-2 rounded-lg border border-zinc-200 bg-zinc-50 text-sm outline-none focus:border-black resize-none"
                />
                <div className="text-[10px] text-zinc-400 mt-1">{notes.length}/1000</div>
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="flex-1 py-2.5 rounded-xl border border-zinc-200 text-sm font-semibold hover:bg-zinc-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  data-testid="report-submit"
                  disabled={busy || !reason}
                  className="flex-1 py-2.5 rounded-xl bg-[#c8102e] hover:bg-[#a50c24] disabled:opacity-50 text-white text-sm font-semibold transition-colors"
                >
                  {busy ? "A enviar..." : "Enviar denúncia"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
